import { cloneElement, CSSProperties, ReactNode } from 'react';
import { cx } from '@linaria/atomic';
import { motion, AnimatePresence, MotionProps } from 'motion/react';
import type { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { mergeClasses } from '../../../helpers';
import { ContentLoader } from '../../loader';
import { useModalContext } from '../provider';
import { modalBox, modalBoxFlex, modalBoxScroll } from './modal-box.styles';

export type ModalBoxProps = CommonHTMLAttributes &
  MotionProps & {
    children: ReactNode;
    flex?: boolean;
    maxWidth?: number;
    minWidth?: number;
    asChild?: boolean;
  };

const maxWidthIncludingMargins = '100vw - 48px';

export const ModalBox = ({
  children,
  asChild,
  className,
  flex,
  minWidth = 0,
  maxWidth = 0,
  style,
  ...rest
}: ModalBoxProps) => {
  const { containerProps, loadingState } = useModalContext();

  /**
   * Only set width-related inline styles when explicit numeric props are provided.
   * Inline styles override CSS classes (higher specificity), so omitting them when
   * not needed allows consumer css-prop-based width overrides to work correctly.
   * The default max-width constraint lives in the `modalBox` Linaria class.
   */
  const widthStyle: CSSProperties = {
    ...(maxWidth > 0 && {
      width: '100%',
      maxWidth: `min(${maxWidth}px, ${maxWidthIncludingMargins})`,
    }),
    ...(minWidth > 0 && {
      minWidth: `min(${minWidth}px, ${maxWidthIncludingMargins})`,
    }),
  };

  const props = {
    className: cx(modalBox, flex ? modalBoxFlex : modalBoxScroll, mergeClasses('wv-modal-box', className)),
    onClick: (e) => e.stopPropagation(),
    ...rest,
    style: { ...widthStyle, ...style },
    ...containerProps,
  };

  if (asChild) {
    const element = cloneElement(children as React.ReactElement, props);

    if (typeof element.type === 'string') {
      const Component = motion[element.type];
      return (
        <AnimatePresence>
          <Component {...element.props} />
        </AnimatePresence>
      );
    }

    return <>{element}</>;
  }

  return (
    <AnimatePresence>
      <motion.section {...props}>
        {children}
        <ContentLoader style={{ borderRadius: theme.borderRadius.medium }} {...loadingState} />
      </motion.section>
    </AnimatePresence>
  );
};
